import * as React from "react";
import {
    ApiQuery,
    FilterMetadata,
    FilterOperators,
} from "~/modules/filters/types";
import { AddFilterButtonFilterList, FilterList } from "./FilterList";
import { ROOT_FILTER_ID } from "../consts";
import { EditorFilter } from "./types";
import { buildEditorFilter } from "./functions/buildEditorFilter";
import { buildFilterFromEditorFilter } from "./functions/buildFilterFromEditorFilter";
import { DndContext, rectIntersection } from "@dnd-kit/core";
import { findParentByChildID } from "./functions/findParentByChildID";
import { findEditorFilterByID } from "./functions/findEditorFilterByID";
import { removeEditorFilterByID } from "./functions/removeEditorFilterByID";
import { isAndOrOperator } from "./functions/isAndOrOperator";
import { removeDuplicateSiblings } from "./functions/removeDuplicateSiblings";
import {
    sortEditorFilter,
    sortEditorFilterArray,
} from "./functions/sortEditorFilter";
import { replaceEditorFilter } from "./functions/replaceEditorFilter";
import { insertAtIndex } from "./functions/insertAtIndex";
import { validateEditorFilter } from "./functions/validateEditorFilter";
import { clearFilterMessages } from "./functions/clearFilterValidations";
import { trackFilterListDragDropReorder } from "../analytics";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { getDefaultNewFilter } from "./getDefaultNewFilter";

// // // //

function getDefaultEditorFilter(props: {
    filterMetadata: FilterMetadata[];
    query: ApiQuery;
}): EditorFilter {
    const { query, filterMetadata } = props;

    const defaultFilter = query.filter
        ? buildEditorFilter({ filter: query.filter, isRoot: true })
        : buildEditorFilter({
              filter: {
                  operator: FilterOperators.And,
                  //   filters: [getDefaultNewFilter(filterMetadata)],
                  filters: [],
              },
              isRoot: true,
          });

    // Add EditorFilter.messages in-place
    validateEditorFilter(defaultFilter);
    return defaultFilter;
}

// // // //

// Top-level component for rendering filters
export function FilterEditor(props: {
    query: ApiQuery;
    onChange: (updatedApiQuery: ApiQuery) => void;
    filterMetadata: FilterMetadata[];
}) {
    // const sensors = useSensors(
    //     useSensor(PointerSensor),
    //     useSensor(KeyboardSensor, {
    //         coordinateGetter: sortableKeyboardCoordinates,
    //     }),
    // );

    const { query, onChange, filterMetadata } = props;

    const [filter, setFilter] = React.useState(getDefaultEditorFilter(props));

    React.useEffect(() => {
        setFilter(getDefaultEditorFilter(props));
    }, [props.query]);

    const isFilterEmpty = filter.nested.length === 0;

    return (
        <div className="w-full min-w-0 max-w-full">
            <DndContext
                id="filter-editor-dnd"
                // sensors={sensors}
                // modifiers={parent === null ? undefined : modifiers}
                // onDragStart={() => setIsDragging(true)}
                // onDragCancel={() => setIsDragging(false)}
                collisionDetection={rectIntersection}
                onDragEnd={({ active, over }) => {
                    // console.log("on drag end");
                    // console.log(active.id);
                    // console.log(over);

                    trackFilterListDragDropReorder();

                    if (!over) {
                        return;
                    }

                    if (active.id === over.id) {
                        return;
                    }

                    const activeFilter = findEditorFilterByID(
                        filter,
                        active.id as string,
                    );

                    const newParentFilter = findEditorFilterByID(
                        filter,
                        over?.id as string,
                    );

                    const oldParentFilter = findParentByChildID(
                        filter,
                        active.id as string,
                    );

                    if (!activeFilter) {
                        return;
                    }

                    if (!newParentFilter) {
                        return;
                    }

                    if (!oldParentFilter) {
                        return;
                    }

                    if (oldParentFilter.id === newParentFilter.id) {
                        return;
                    }

                    // Remove the active filter entirely
                    const withoutActive = removeEditorFilterByID(
                        filter,
                        active.id as string,
                    );

                    // Check if the newParentFilter is NOT an and/or => handle edge case
                    if (!isAndOrOperator(newParentFilter.state.operator)) {
                        // Find the parent of the newParent
                        const newStepParent = findParentByChildID(
                            filter,
                            newParentFilter.id,
                        );
                        if (!newStepParent) {
                            return;
                        }

                        // Find new index (inserts activeFilter ABOVE newParentFilter)
                        const newStepParentNestedIDs = newStepParent.nested.map(
                            (e) => e.id,
                        );
                        const newParentIndex = newStepParentNestedIDs.indexOf(
                            newParentFilter.id,
                        );

                        const updatedStepParent = {
                            ...newStepParent,
                            nested: [
                                ...insertAtIndex({
                                    array: newStepParent.nested,
                                    index: newParentIndex,
                                    item: activeFilter,
                                }),
                            ],
                        };

                        // Update filters
                        const updatedFilter = removeDuplicateSiblings(
                            sortEditorFilter(
                                replaceEditorFilter(
                                    withoutActive,
                                    updatedStepParent,
                                ),
                            ),
                        );

                        setFilter(
                            validateEditorFilter(
                                clearFilterMessages({
                                    ...updatedFilter,
                                    id: ROOT_FILTER_ID,
                                }),
                            ),
                        );
                        return;
                    }

                    // Update new parent
                    const updatedNewParent = {
                        ...newParentFilter,
                        nested: [...newParentFilter.nested, activeFilter],
                    };

                    // Update filters
                    const updatedFilter = removeDuplicateSiblings(
                        sortEditorFilter(
                            replaceEditorFilter(
                                withoutActive,
                                updatedNewParent,
                            ),
                        ),
                    );

                    setFilter(
                        validateEditorFilter(
                            clearFilterMessages({
                                ...updatedFilter,
                                id: ROOT_FILTER_ID,
                            }),
                        ),
                    );
                }}
            >
                {!isFilterEmpty && (
                    <FilterList
                        filter={filter}
                        filterMetadata={filterMetadata}
                        depth={0}
                        onRemove={() => {
                            onChange({
                                ...query,
                                filter: undefined,
                            });
                        }}
                        onChange={(updatedFilter: EditorFilter) => {
                            // Remove parent filter if it has no filters left - keep this?
                            // if (updatedFilter.nested?.length === 0) {
                            //     onChange({
                            //         ...query,
                            //         filter: undefined,
                            //     });
                            //     setFilter(validateEditorFilte)r(
                            //         getDefaultEditorFilter({
                            //             ...props,
                            //             currentFilter: null,
                            //         }),
                            //     );
                            //     return;
                            // }

                            setFilter(
                                validateEditorFilter(
                                    clearFilterMessages({
                                        ...updatedFilter,
                                        id: ROOT_FILTER_ID,
                                    }),
                                ),
                            );
                            onChange({
                                ...query,
                                filter: buildFilterFromEditorFilter(
                                    updatedFilter,
                                ),
                            });
                        }}
                    />
                )}

                {isFilterEmpty && (
                    <div
                        className="
            relative w-full h-full flex flex-col justify-center items-center
            rounded-lg border-2 border-dashed p-6 text-center text-muted-foreground gap-4"
                    >
                        <FontAwesomeIcon
                            className="mx-auto h-8 w-8"
                            icon={faMagnifyingGlass}
                        />
                        <span className="block text-lg font-light select-none">
                            Add a condition to filter the data
                        </span>
                        <AddFilterButtonFilterList
                            compact
                            filterMetadata={filterMetadata}
                            addFilter={(selectedFilter) => {
                                const updatedFilter = {
                                    ...filter,
                                    nested: sortEditorFilterArray([
                                        ...filter.nested,
                                        buildEditorFilter({
                                            filter: getDefaultNewFilter([
                                                selectedFilter,
                                            ]),
                                            isRoot: false,
                                        }),
                                    ]),
                                };

                                setFilter(
                                    validateEditorFilter(
                                        clearFilterMessages({
                                            ...updatedFilter,
                                            id: ROOT_FILTER_ID,
                                        }),
                                    ),
                                );
                                onChange({
                                    ...query,
                                    filter: buildFilterFromEditorFilter(
                                        updatedFilter,
                                    ),
                                });
                            }}
                            currentOperator={"and"}
                        />
                    </div>
                )}
                {/* <DraggableOverlay filter={filter} /> */}
            </DndContext>
        </div>
    );
}
